<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BillingRule',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'saved', 'cancelEdit']);

  const props = defineProps({
    ...userComponentProps,
    item: {
      type: Object,
      required: false,
    },
    editing: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const formId = ref(null);
  const formName = ref('billingRule');
  const labelAlign = ref('right');
  const metadata = ref(null);
  const container = ref(null);
  const resource = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    formId: formId.value,
    formName: formName.value,
    labelAlign: labelAlign.value,
    metadata: metadata.value,
    container: container.value,
    resource: resource.value,
    ux: ux.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_metadata = () => {
    return $app.value.repo.get('ui_metadata');
  };
  const $$load_container = () => {
    return {
      self: $fn.value.urlFor('create_object_by_type', {
        object_type: 'billing-rule',
      }),
      object: 'list',
      data: [],
    };
  };
  const $$load_resource = () => {
    return new $fn.value.Resource(
      props.item ? props.item : { rule_type: null, rule: {} }
    );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'formName' },
      { name: 'labelAlign' },
      { name: 'metadata' },
      { name: 'container' },
      { name: 'resource' },
      { name: 'ux' },
    ],
    loaders: {
      formId: $$load_formId,
      metadata: $$load_metadata,
      container: $$load_container,
      resource: $$load_resource,
    },
    ux: {},
    dataRefs: {
      formId,
      formName,
      labelAlign,
      metadata,
      container,
      resource,
      ux,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const ruleTypes = computed(() => {
    if (loaded.value) {
      return metadata.value.billing_rule_type_options;
    }
    return null;
  });
  const editor = computed(() => {
    if (loaded.value) {
      return props.editing ? resource.value.editor() : null;
    }
    return null;
  });
  const target = computed(() => {
    if (loaded.value) {
      return editor.value || resource.value;
    }
    return null;
  });

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.editing,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('editing');
    }
  );

  const act_2248226175642056_emit_z1b9pzrf9_getActionArgs = async ($event) => {
    return {
      event: 'saved',
      value: $event.data.response.data,
    };
  };
  const act_2248226175642056_rest_7o04pntnn_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: container.value,
      resource: editor.value,
    };
  };
  const act_6993263378944638_emit_2vjbgx6np_getActionArgs = async ($event) => {
    return {
      event: 'cancelEdit',
    };
  };

  const act_7315092382398562_reloadComponentData_oqelj3ofl =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_svozn4dxz =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_oqelj3ofl,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_svozn4dxz],
    event: 'reload',
  });
  const act_2248226175642056_emit_z1b9pzrf9 = new actionTypes.EmitAction({
    actionArgs: act_2248226175642056_emit_z1b9pzrf9_getActionArgs,
  });
  const inst_evh_2248226175642056_saved_tag4oe2sv =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_emit_z1b9pzrf9,
      event: 'saved',
      displayName: 'emit',
    });
  const act_2248226175642056_rest_7o04pntnn = new actionTypes.RestAction({
    actionArgs: act_2248226175642056_rest_7o04pntnn_getActionArgs,
    displayName: 'save',
    events: [inst_evh_2248226175642056_saved_tag4oe2sv],
  });
  const inst_evh_2248226175642056_submit_62f0nhuve =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_rest_7o04pntnn,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_2248226175642056_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_submit_62f0nhuve],
    event: 'submit',
  });
  const act_6993263378944638_emit_2vjbgx6np = new actionTypes.EmitAction({
    actionArgs: act_6993263378944638_emit_2vjbgx6np_getActionArgs,
  });
  const inst_evh_6993263378944638_clickPreventStop_az4kuktz4 =
    new actionTypes.EventHandlerImpl({
      action: act_6993263378944638_emit_2vjbgx6np,
      event: 'click.prevent.stop',
      displayName: 'emit',
    });
  const inst_evh_6993263378944638_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6993263378944638_clickPreventStop_az4kuktz4],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_svozn4dxz,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_saved_tag4oe2sv,
    inst_evh_2248226175642056_submit_62f0nhuve,
    save: inst_evh_2248226175642056_submit_62f0nhuve,
    inst_evh_2248226175642056_submit,
    inst_evh_6993263378944638_clickPreventStop_az4kuktz4,
    inst_evh_6993263378944638_clickPreventStop,
  };

  const evh_7315092382398562_reload_svozn4dxz = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_svozn4dxz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_saved_tag4oe2sv = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_saved_tag4oe2sv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_submit_62f0nhuve = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_submit_62f0nhuve.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_submit = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6993263378944638_clickPreventStop_az4kuktz4 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6993263378944638_clickPreventStop_az4kuktz4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6993263378944638_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_6993263378944638_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpForm
      :id="formId"
      :name="formName"
      :readonly="!editing"
      class="row"
      v-if="loaded"
      @submit="evh_2248226175642056_submit($event, {})"
    >
      <ItpCol>
        <ItpFormGroup
          :label-cols-md="3"
          :content-cols-md="9"
          name="delivery_modes"
          :label="pgettext('BillingRule', 'Rule type')"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormNativeSelect
            name="rule_type"
            v-model="target.rule_type"
            :options="ruleTypes"
            valueField="value"
            textField="name"
            required
          >
          </ItpFormNativeSelect>
        </ItpFormGroup>
        <BillingDateRule
          :item="target.rule"
          :metadata="metadata"
          :editing="editing"
          v-if="target.rule_type == 'billing_date'"
        >
        </BillingDateRule>
        <BillingDateRule
          :item="target.rule"
          :metadata="metadata"
          :editing="editing"
          v-if="target.rule_type == 'due_date'"
        >
        </BillingDateRule>
        <ExchangeRateDateRule
          :item="target.rule"
          :metadata="metadata"
          :editing="editing"
          v-if="target.rule_type == 'exchange_rate_date'"
        >
        </ExchangeRateDateRule>
        <ItpBox justifyContent="end">
          <ItpButton
            :text="pgettext('Button', 'Save')"
            type="submit"
            :form="forms[formName]"
            variant="primary"
            :spinning="actions.save && actions.save.isRunning"
            :disabled="!forms[formName] || !forms[formName].submittable"
            v-if="editing"
          >
          </ItpButton>
          <ItpButton
            :text="gettext('Cancel')"
            class="ms-2"
            v-if="editing"
            @click.prevent.stop="
              evh_6993263378944638_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </ItpCol>
    </ItpForm>
  </ItpUsercontrol>
</template>
